import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, LogIn, LogOut, Printer } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { toast } from 'sonner';
import { Button, Card, Dialog, Field, Input, Spinner, Textarea } from '@/components/ui';
import { errorMessage, get, post } from '@/lib/api';
import { cls, dateTime, money } from '@/lib/format';

interface MoneyLine { method: string; methodName: string; terminal: string | null; count: number; net: string; needsSlip: boolean; pending?: number }
export interface MyShift {
  free: boolean;
  required: boolean;
  autoCloseHours: number;
  open: { id: string; startedAt: string; startBusinessDate: string } | null;
  notes: { by: string; at: string; note: string }[];
  money: { openedAt: string; lines: MoneyLine[]; totalNet: string } | null;
}
interface ShiftReport {
  id: string;
  fullName: string;
  departmentName: string | null;
  startedAt: string;
  endedAt: string | null;
  closedBy: 'SELF' | 'SYSTEM' | null;
  handoverNote: string | null;
  summary: { counts: { label: string; count: number }[]; recent: { at: string; text: string }[] };
  money: { lines: (MoneyLine & { declared: string; difference: string })[]; totalNet: string; totalDeclared: string; totalDifference: string; note: string | null } | null;
}

const hhmm = (iso: string) => new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
const hoursSince = (iso: string) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return `${Math.floor(m / 60)} h ${m % 60} min`;
};

export function useMyShift() {
  return useQuery({ queryKey: ['my-shift'], queryFn: () => get<MyShift>('/work-shifts/mine'), refetchInterval: 60_000 });
}

/** The top bar's shift button (owner, 2026-10-29): start it, or see since when and close it. */
export function ShiftButton() {
  const { data } = useMyShift();
  const [closing, setClosing] = useState(false);
  if (!data || data.free) return null;
  if (!data.open) return <StartButton compact />;
  return (
    <>
      <button
        type="button"
        onClick={() => setClosing(true)}
        title="Close my shift"
        className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-brand-200 bg-brand-50 px-2.5 py-1 text-[12px] font-medium text-brand-800 hover:bg-brand-100"
      >
        <Clock className="size-3.5" /> On shift since {hhmm(data.open.startedAt)}
      </button>
      {closing && <CloseShiftDialog shift={data} onClose={() => setClosing(false)} />}
    </>
  );
}

function StartButton({ compact }: { compact?: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  async function start() {
    setBusy(true);
    try {
      await post('/work-shifts/start');
      toast.success('Shift started');
      await qc.invalidateQueries({ queryKey: ['my-shift'] });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Button size={compact ? 'sm' : 'md'} variant="primary" icon={<LogIn className="size-4" />} loading={busy} onClick={() => void start()}>
      Start my shift
    </Button>
  );
}

/**
 * Nothing is done outside a shift (owner, 2026-10-29): until the shift is started, the page waits behind this panel. The
 * account's own pages (password, two-step sign-in) stay open.
 */
export function ShiftGate({ children }: { children: ReactNode }) {
  const { data } = useMyShift();
  const { pathname } = useLocation();
  if (!data || data.free || !data.required || data.open || pathname.startsWith('/account')) return <>{children}</>;
  return (
    <div className="mx-auto mt-10 max-w-xl">
      <Card title="Start your shift">
        <p className="text-sm text-slate-600">Every piece of work in the system is done inside a shift: start it now, and close it when you finish, with a note for the next person.</p>
        {!!data.notes.length && (
          <div className="mt-4">
            <h4 className="mb-1 text-[13px] font-semibold text-slate-700">Notes left for your department</h4>
            <ul className="space-y-2 text-sm">
              {data.notes.map((n, i) => (
                <li key={i} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-xs text-slate-500">{n.by}, {dateTime(n.at)}</div>
                  <div className="whitespace-pre-line">{n.note}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-4"><StartButton /></div>
        <p className="mt-3 text-xs text-slate-500">A shift left open is closed by the system after {data.autoCloseHours} hours and shown to your head as not closed by you.</p>
      </Card>
    </div>
  );
}

/** Closing the shift: what was done, the takings against the slips (for those who take money), the note for the next person. */
function CloseShiftDialog({ shift, onClose }: { shift: MyShift; onClose: () => void }) {
  const qc = useQueryClient();
  const open = shift.open!;
  const report = useQuery({ queryKey: ['work-shift', open.id], queryFn: () => get<ShiftReport>(`/work-shifts/${open.id}`) });
  const lines = shift.money?.lines ?? [];
  const [slips, setSlips] = useState<Record<string, string>>({});
  const [moneyNote, setMoneyNote] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const key = (l: MoneyLine) => `${l.method}|${l.terminal ?? ''}`;
  const diff = lines.filter((l) => l.needsSlip).reduce((a, l) => a + (Number(slips[key(l)] || 0) - Number(l.net)), 0);
  async function close() {
    setBusy(true);
    try {
      const declared = lines.filter((l) => l.needsSlip).map((l) => ({ method: l.method, terminal: l.terminal, amount: slips[key(l)] || '0' }));
      const r = await post<{ id: string }>('/work-shifts/close', { note, declared: lines.length ? declared : undefined, moneyNote: moneyNote || undefined });
      toast.success('Shift closed');
      setDone(r.id);
      await qc.invalidateQueries({ queryKey: ['my-shift'] });
      void qc.invalidateQueries({ queryKey: ['fo-shift'] });
      void qc.invalidateQueries({ queryKey: ['fo-shifts'] });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  if (done) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()} title="Shift closed" footer={<Button onClick={onClose}>Done</Button>}>
        <p className="text-sm">Your shift is closed and your note is left for the next person.</p>
        <Link to={`/shift-report/${done}`} onClick={onClose} className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-700 underline"><Printer className="size-4" /> Open and print the shift report</Link>
      </Dialog>
    );
  }
  return (
    <Dialog
      open
      wide
      onOpenChange={(o) => !o && onClose()}
      title={`Close my shift: since ${dateTime(open.startedAt)} (${hoursSince(open.startedAt)})`}
      description="What you did in this shift is listed below. Leave a note for the next person, then close."
      footer={
        <>
          <Button onClick={onClose}>Back</Button>
          <Button variant="primary" icon={<LogOut className="size-4" />} loading={busy} disabled={note.trim().length < 3} onClick={() => void close()}>Close my shift</Button>
        </>
      }
    >
      <div className="grid gap-4">
        <section>
          <h4 className="mb-1 text-[13px] font-semibold text-slate-700">What you did</h4>
          {!report.data ? <Spinner /> : !report.data.summary.counts.length ? <p className="text-sm text-slate-500">Nothing recorded in this shift.</p> : (
            <ul className="grid gap-x-6 text-sm sm:grid-cols-2">
              {report.data.summary.counts.map((c) => <li key={c.label} className="flex justify-between border-b border-slate-100 py-0.5"><span>{c.label}</span><span className="num">{c.count}</span></li>)}
            </ul>
          )}
        </section>
        {!!lines.length && (
          <section>
            <h4 className="mb-1 text-[13px] font-semibold text-slate-700">Your takings: type each terminal's end-of-shift slip total</h4>
            <table className="table-dense w-full">
              <thead><tr><th>Method</th><th>Terminal</th><th className="text-right">Taken (net)</th><th className="text-right">Slip total</th></tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={key(l)}>
                    <td>{l.methodName}{l.pending ? <div className="text-xs text-amber-700">{l.pending} waiting for Finance</div> : null}</td>
                    <td>{l.terminal ?? '—'}</td>
                    <td className="num text-right">{money(l.net)}</td>
                    <td className="text-right">{l.needsSlip ? <Input className="ml-auto h-8 w-32 text-right" inputMode="decimal" value={slips[key(l)] ?? ''} onChange={(e) => setSlips({ ...slips, [key(l)]: e.target.value.replace(/[^\d.]/g, '') })} placeholder="from the slip" /> : <span className="text-xs text-slate-500">no slip</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {Math.abs(diff) > 0.004 && (
              <Field label={`The slips differ by ${money(diff.toFixed(2))}: what happened?`} className="mt-2">
                <Textarea rows={2} value={moneyNote} onChange={(e) => setMoneyNote(e.target.value)} />
              </Field>
            )}
            <p className="mt-1 text-xs text-slate-500">Hand the printed slips to the General Cashier.</p>
          </section>
        )}
        <section>
          <Field label="Note for the next person">
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Room 214 asked for a late check-out at 2 pm; the printer in the restaurant is low on paper" />
          </Field>
          <button type="button" className={cls('mt-1 text-xs text-brand-700 underline')} onClick={() => setNote('Nothing to hand over')}>Nothing to hand over</button>
        </section>
      </div>
    </Dialog>
  );
}

/** One shift's report, printable: what was done, the takings, the note left (owner, 2026-10-29). */
export function ShiftReportView({ id }: { id: string }) {
  const { data, isLoading } = useQuery({ queryKey: ['work-shift', id], queryFn: () => get<ShiftReport>(`/work-shifts/${id}`) });
  if (isLoading || !data) return <Spinner />;
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Shift report: {data.fullName}</h1>
          <p className="text-sm text-slate-600">
            {data.departmentName ?? ''} · {dateTime(data.startedAt)} to {data.endedAt ? dateTime(data.endedAt) : 'still open'}
            {data.closedBy === 'SYSTEM' && <b className="text-amber-700"> · closed by the system: not closed by them</b>}
          </p>
        </div>
        <Button icon={<Printer className="size-4" />} onClick={() => window.print()}>Print</Button>
      </div>
      <Card title="What was done">
        {!data.summary.counts.length ? <p className="text-sm text-slate-500">Nothing recorded.</p> : (
          <ul className="grid gap-x-6 text-sm sm:grid-cols-2">
            {data.summary.counts.map((c) => <li key={c.label} className="flex justify-between border-b border-slate-100 py-0.5"><span>{c.label}</span><span className="num">{c.count}</span></li>)}
          </ul>
        )}
        {!!data.summary.recent.length && (
          <>
            <h4 className="mb-1 mt-3 text-[13px] font-semibold text-slate-700">Latest actions</h4>
            <ul className="text-xs text-slate-600">{data.summary.recent.map((r, i) => <li key={i}>{hhmm(r.at)} {r.text}</li>)}</ul>
          </>
        )}
      </Card>
      {data.money && (
        <Card title={`Takings: ${money(data.money.totalNet)} · slips ${money(data.money.totalDeclared)} · difference ${money(data.money.totalDifference)}`}>
          <table className="table-dense w-full">
            <thead><tr><th>Method</th><th>Terminal</th><th className="text-right">Taken</th><th className="text-right">Slip</th><th className="text-right">Difference</th></tr></thead>
            <tbody>{data.money.lines.map((l) => <tr key={`${l.method}|${l.terminal ?? ''}`}><td>{l.methodName}</td><td>{l.terminal ?? '—'}</td><td className="num text-right">{money(l.net)}</td><td className="num text-right">{money(l.declared)}</td><td className="num text-right">{money(l.difference)}</td></tr>)}</tbody>
          </table>
          {data.money.note && <p className="mt-2 text-sm">“{data.money.note}”</p>}
        </Card>
      )}
      <Card title="Note left for the next person"><p className="whitespace-pre-line text-sm">{data.handoverNote ?? (data.closedBy === 'SYSTEM' ? 'None: the system closed the shift.' : 'Still open.')}</p></Card>
    </div>
  );
}
